import React, { useState } from 'react'
import {
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from '@mui/material'
import FixedWidthContainer from 'components/FixedWidthContainer'
import { useLocation, useParams } from 'react-router-dom'
import { itemCategories } from 'constants/categoriesData'
import ProductList from 'pages/Cabinets/FilteredProductList'
import useAllProducts from 'hooks/useAllProducts'
import SectionHeader from 'components/SectionHeader'
import { useTranslation } from 'react-i18next'

const CabinetPage = () => {
  const { color } = useParams<{ color: string }>()
  const location = useLocation()
  const { imageUrl, specification } = location.state || {}
  const [selectedItemCategory, setSelectedItemCategory] = useState<
    string | null
  >(itemCategories[0]?.label || null)
  const [selectedCabinetType, setSelectedCabinetType] = useState<string | null>(
    null
  )
  const { t } = useTranslation()

  const categoryLabelMap: { [key: string]: string } = {
    'Base Cabinets': t('itemCategories.baseCabinets'),
    'Wall Cabinets': t('itemCategories.wallCabinets'),
    'Oven and Pantry Cabinets': t('itemCategories.ovenAndPantryCabinets'),
    'Bathroom Vanities': t('itemCategories.bathroomVanities'),
    'Trim & Details': t('itemCategories.trimAndDetails'),
    Accessories: t('itemCategories.accessories')
  }

  const { allProducts } = useAllProducts(
    'Cabinets',
    color,
    selectedItemCategory ?? undefined
  )

  const handleCabinetTypeClick = (cabinetType: string) => {
    setSelectedCabinetType(prev => (prev === cabinetType ? null : cabinetType))
  }

  const handleItemCategoryClick = (itemCategory: string) => {
    setSelectedItemCategory(prev =>
      prev === itemCategory ? null : itemCategory
    )
    setSelectedCabinetType(null)
  }

  return (
    <Box>
      <FixedWidthContainer sx={{ my: '50px' }}>
        <SectionHeader title={t('cabinet.shopByCategory')} />
        <Box sx={{ width: '100%' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
              {itemCategories.map(category => (
                <Box
                  key={category.label}
                  sx={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      '& .default-icon': {
                        opacity: 0
                      },
                      '& .hover-icon': {
                        opacity: 1
                      },
                      '& p': {
                        color: 'primary.main'
                      }
                    }
                  }}
                  onClick={() => handleItemCategoryClick(category.label)}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: category.width,
                      height: category.height,
                      margin: '0 auto',
                      mb: 1
                    }}
                  >
                    <Box
                      className='default-icon'
                      component='img'
                      src={category.icon}
                      alt={category.label}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: category.width,
                        height: category.height,
                        opacity:
                          selectedItemCategory === category.label ? 0 : 1,
                        transition: 'opacity 0.2s ease-in-out'
                      }}
                    />
                    <Box
                      className='hover-icon'
                      component='img'
                      src={category.hoverIcon}
                      alt={category.label}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: category.width,
                        height: category.height,
                        opacity:
                          selectedItemCategory === category.label ? 1 : 0,
                        transition: 'opacity 0.2s ease-in-out'
                      }}
                    />
                  </Box>

                  <Typography
                    variant='body1'
                    sx={{
                      textAlign: 'center',
                      color:
                        selectedItemCategory === category.label
                          ? 'primary.main'
                          : '#000000',
                      cursor: 'pointer'
                    }}
                  >
                    {categoryLabelMap[category.label] || category.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Divider
          sx={{
            width: '100%',
            margin: '20px 0px'
          }}
        />

        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography
                  variant='h2'
                  sx={{
                    fontFamily: 'Playfair Display SC',
                    letterSpacing: '0.1em',
                    mt: '30px'
                  }}
                >
                  {t('cabinet.cabinetType')}
                </Typography>
                <Box
                  sx={{
                    maxHeight: '500px',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '5px'
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#939393',
                      borderRadius: '5px',
                      opacity: '0px',
                      transform: 'rotate(-90deg)'
                    },
                    width: '230px',
                    mt: '20px'
                  }}
                >
                  <Stack spacing={2} sx={{ width: '220px', p: '0px' }}>
                    {Object.keys(allProducts).map(classKey =>
                      Object.keys(allProducts[classKey]).map(colorKey =>
                        Object.keys(allProducts[classKey][colorKey]).map(
                          itemCategoryKey =>
                            (selectedItemCategory === null ||
                              selectedItemCategory === itemCategoryKey) &&
                            Object.keys(
                              allProducts[classKey][colorKey][itemCategoryKey]
                            ).map((cabinetTypeKey, index) => (
                              <ListItem
                                key={index}
                                sx={{
                                  padding: '0px',
                                  fontFamily: 'Inter',
                                  fontSize: '16px',
                                  fontWeight:
                                    selectedCabinetType === cabinetTypeKey
                                      ? '700'
                                      : '400',
                                  lineHeight: '48px',
                                  textAlign: 'left',
                                  color:
                                    selectedCabinetType === cabinetTypeKey
                                      ? 'primary.main'
                                      : '#000000',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    '.MuiListItemText-primary': {
                                      color: 'primary.main',
                                      cursor: 'pointer',
                                      fontWeight: '700'
                                    }
                                  },
                                  ...(selectedCabinetType ===
                                    cabinetTypeKey && {
                                    '.MuiListItemText-primary': {
                                      color: 'primary.main',
                                      cursor: 'pointer',
                                      fontWeight: '700'
                                    }
                                  })
                                }}
                                onClick={() =>
                                  handleCabinetTypeClick(cabinetTypeKey)
                                }
                              >
                                <ListItemText
                                  primary={cabinetTypeKey
                                    .replace(/([A-Z])/g, ' $1')
                                    .trim()}
                                />
                              </ListItem>
                            ))
                        )
                      )
                    )}
                  </Stack>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography
                variant='h2'
                sx={{
                  fontFamily: 'Playfair Display SC',
                  letterSpacing: '0.1em',
                  margin: '30px 0px'
                }}
              >
                {t('cabinet.itemTitles')}
              </Typography>
              <ProductList
                allProducts={allProducts}
                selectedItemCategory={selectedItemCategory}
                selectedCabinetType={selectedCabinetType}
              />
            </Grid>
          </Grid>
        </Box>
        <Box
          sx={{
            marginTop: Object.keys(allProducts).length === 0 ? '100vh' : '0vh'
          }}
        >
          <SectionHeader title={color} />

          <Box
            sx={{
              display: 'flex',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                width: '50%',
                zIndex: 1
              }}
            >
              <Box
                component='img'
                src={imageUrl}
                alt='Product Image'
                sx={{
                  // Sample-door images are ~321px wide at natural size; clamp
                  // so a larger source file can't blow up the layout.
                  width: '100%',
                  maxWidth: '321px',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </Box>

            <Box sx={{ width: '50%', zIndex: 1 }}>
              <Typography
                variant='h2'
                sx={{
                  fontFamily: 'Playfair Display SC',
                  letterSpacing: '0.1em',
                  ml: '100px'
                }}
              >
                {t('cabinet.specifications')}
              </Typography>

              <List sx={{ listStyleType: 'disc', marginLeft: '115px' }}>
                {specification
                  ?.split('\n')
                  .map((line: string, index: number) => (
                    <ListItem
                      key={index}
                      sx={{ display: 'list-item', padding: 0 }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant='body1'
                            sx={{
                              lineHeight: '28.8px'
                            }}
                          >
                            {line}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
              </List>
            </Box>
          </Box>
        </Box>
      </FixedWidthContainer>
      <Box
        sx={{ width: '100%', display: 'block' }}
        component='img'
        src='/bottom_image.png'
        alt='Bottom Image'
      />
    </Box>
  )
}

export default CabinetPage
